import { useEffect, useState } from 'react';
import { SUPABASE_URL } from '../supabase';
/* Presentational only — it reads no store, which is what keeps this page
   independent of the app it is bundled with. */
import Icon from './Icon';
import { summaryFromGrid, pctPassed, EXEC_LABELS, type ExecSummary } from '../exec-summary';

/**
 * THE PAGE SOMEBODY OPENS WHO HAS NO ACCOUNT — #216.
 *
 * TRINRE's approvers are a client's sponsor, an executive director, a
 * department head. None of them will be given a login, so until now their
 * answer reached the sheet second-hand: somebody in the office typed it for
 * them. That is a note saying they agreed. This is the thing itself.
 *
 * WHY IT IS NOT PART OF THE APP, though it is in the same bundle. Nothing here
 * signs in, reads the store, or subscribes to anything — it is mounted in
 * main.tsx BEFORE <App/>, so a person on this page never starts a session, and
 * a broken session can never keep them off it. The only thing it can reach is
 * one function, and the only thing it holds is the token out of the address.
 *
 * THE SHAPE IS THE ONE VINNIE CHOSE ON 22 AUGUST — option B, the cover sheet,
 * with the gate on. A summary of what this is and who else is signing, the
 * document opening from it, the two answers underneath. It answers the question
 * an approver actually asks — has the CIO signed this yet? — which the document
 * on its own cannot.
 */
const SIGN = `${SUPABASE_URL}/functions/v1/sign`;

type Answer = 'approved' | 'rejected' | 'waiting';

type Row = { position: string; name: string; answer: Answer; at: string | null; why: string; isYou: boolean };

/* ExecSummary and the parse live in ../exec-summary — a completed test-script
   workbook's own Execution Summary sheet, surfaced as a pass/fail read-out. */

type Sheet = {
  state: 'ready' | 'answered' | 'signed' | 'rejected' | 'withdrawn' | 'superseded' | 'changed' | 'gone';
  you?: {
    name: string;
    position: string;
    answer: Answer;
    at: string | null;
    why: string;
    opened: boolean;
    /* Spreadsheet test script only: this signer is the tester who completes and
       uploads the workbook (the first stage), and whether they already have. A
       downstream approver has mustUpload = false and only downloads + signs. */
    mustUpload?: boolean;
    uploaded?: boolean;
  };
  /* Spreadsheet test script: the subject is completed by DOWNLOAD → fill offline
     → UPLOAD the completed workbook → sign, because an Excel file cannot be shown
     in the browser. Set by the /sign function from the document's kind + type. */
  signByUpload?: boolean;
  /* Present once a completed workbook has been uploaded — surfaced as a pass/fail
     read-out so an approver reads the result without opening Excel. */
  summary?: ExecSummary | null;
  document?: {
    /* Which subject this sheet is against. Absent on older replies is treated as
       a document, so nothing regresses. */
    subjectKind?: 'document' | 'sprint' | 'release';
    board: string;
    organisation: string;
    title: string;
    kind?: string;
    version?: number;
    sentAt: string | null;
    /* Document only. */
    fileName?: string;
    fileType?: string;
    fileSize?: number;
    /* Sprint only. */
    startsOn?: string | null;
    endsOn?: string | null;
    /* Sprint and release: what is in it, shown read-only. */
    items?: { ref: string; title: string; type: string; status: string }[];
  };
  panel?: Row[];
  fingerprint?: { askedAgainst: string; now: string };
  url?: string;
  error?: string;
};

/** The reader's own clock, because "22 August at 4:31pm" is only true where they are. */
const when = (at: string | null) =>
  at
    ? new Date(at).toLocaleString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

const onThe = (at: string | null) =>
  at
    ? new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

const size = (bytes: number) =>
  bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`;

/** A hash is unreadable; four groups of four is comparable by eye. */
const inFours = (hash: string) => (hash.slice(0, 16).match(/.{1,4}/g) ?? []).join(' ');

/* THE COMPLETED WORKBOOK UPLOAD — the one file this page may send back.
   Capped and typed here as well as in the /sign function, so an honest mistake
   is caught before a 25 MB base64 body goes over the wire. The accepted types
   are the two Excel workbook types the board accepts for a test script. */
const UPLOAD_CAP = 25_000_000;
const XLSX_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];
/** A browser sometimes hands over an empty type for a .xlsx; fall back to the
 *  extension so a correct workbook is never turned away on type alone. */
const looksLikeWorkbook = (f: File) =>
  XLSX_TYPES.includes(f.type) || /\.xlsx?$/i.test(f.name);

/** The file as base64 (no data: prefix) for the JSON body the /sign function reads. */
const toBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(f);
  });

/**
 * THE EXECUTION SUMMARY, READ FROM THE WORKBOOK — in the tester's browser, where
 * the xlsx library already lives, so the edge function stays import-free. The grid
 * → figures mapping lives in ../exec-summary (proven at commit time); this only
 * turns the chosen sheet into a grid. Loaded on demand so it never weighs on the
 * signing page's first paint, and a failure yields null (no read-out, never a
 * wrong one).
 */
async function parseExecSummary(file: File): Promise<ExecSummary | null> {
  try {
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheetName = wb.SheetNames.find((n) => /exec|summary/i.test(n));
    if (!sheetName) return null;
    const grid = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], {
      header: 1,
      blankrows: false,
      defval: '',
    }) as unknown[][];
    return summaryFromGrid(grid);
  } catch {
    return null;
  }
}

/** The people still to sign, by title, as "the QA Lead and the Project Manager" —
 *  so a tester who has just signed off is told where it goes next. Empty when
 *  nobody is left waiting. */
const nextToSign = (panel: Row[]): string => {
  const left = panel
    .filter((r) => !r.isYou && r.answer === 'waiting')
    .map((r) => (r.position ?? '').trim())
    .filter(Boolean)
    .map((p) => `the ${p}`);
  if (left.length === 0) return '';
  if (left.length === 1) return left[0];
  if (left.length === 2) return `${left[0]} and ${left[1]}`;
  return `${left.slice(0, -1).join(', ')} and ${left[left.length - 1]}`;
};

/** A delivered item, marked by form as well as colour (a check, not just a
 *  green word) — the board's "Done" column, however it is cased. */
const isDone = (status: string) => (status ?? '').trim().toLowerCase() === 'done';

export default function Signing({ token }: { token: string }) {
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  /* Null while nothing is being answered; the page is otherwise read-only. */
  const [saying, setSaying] = useState<'approved' | 'rejected' | null>(null);
  const [why, setWhy] = useState('');
  const [file, setFile] = useState('');
  /* The completed-workbook upload (spreadsheet test script). Its own busy flag and
     its own error line, so a rejected upload never reads as a failed sign-off. */
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState('');

  const ask = async (body: Record<string, unknown>): Promise<Sheet> => {
    const r = await fetch(SIGN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, ...body }),
    });
    return r.json();
  };

  useEffect(() => {
    let stop = false;
    ask({ action: 'look' })
      .then((s) => !stop && setSheet(s))
      /* A page that shows a spinner for ever is worse than one that says the
         link did not work — see the wording below, which reveals nothing. */
      .catch(() => !stop && setSheet({ state: 'gone' }));
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* DON'T LET THE WINDOW CLOSE MID-UPLOAD. Closing before and after an upload is
     safe (the link re-opens the page and nothing is lost), so there is no blanket
     warning — only while bytes are actually in flight, where closing would abandon
     a transfer the page cannot resume on its own. */
  useEffect(() => {
    if (!uploading) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  if (!sheet) return <div className="sg-wait">Opening…</div>;

  /*
   * ONE WORDING FOR EVERY LINK THAT IS NOT A LIVE ONE.
   *
   * A wrong link, an expired one and a request that has been taken back all
   * land here, because telling them apart would make a wrong link a way to find
   * out that a document exists. It says what to do instead, which is the only
   * useful thing it can say.
   */
  if (sheet.state === 'gone')
    return (
      <Paper masthead="LINK NOT AVAILABLE">
        <p className="sg-stamp is-bad">
          <strong>This link has expired, or is not one we recognise.</strong>
        </p>
        <p className="sg-note">
          Ask whoever sent it to send another. Nothing about the document is shown here — a wrong
          link must not become a way to read one.
        </p>
      </Paper>
    );

  const doc = sheet.document!;
  const subjectKind = doc.subjectKind ?? 'document';
  const you = sheet.you!;
  const panel = sheet.panel ?? [];
  /* A spreadsheet test script signs by completing the workbook, not by reading a
     file in place. The tester (mustUpload) downloads the blank, fills it in and
     uploads it; a downstream approver downloads the completed workbook and signs.
     Either way the "review before you answer" gate is a download, not a preview. */
  const signByUpload = subjectKind === 'document' && !!sheet.signByUpload;
  const mustUpload = signByUpload && !!you.mustUpload;
  const uploaded = !!you.uploaded;
  const summary = sheet.summary ?? null;
  /* A tester reads truer signing OFF than approving; the words follow through to
     the buttons, the confirmation and the stamp. */
  const yesLabel = signByUpload ? 'Sign off' : 'Approve';
  const noLabel = signByUpload ? 'Didn’t pass' : 'Reject';
  /* The gate the answer is held behind: a tester must have UPLOADED the completed
     workbook; everyone else must have OPENED (read / downloaded) the subject. */
  const readyToAnswer = mustUpload ? uploaded : you.opened;

  if (sheet.state === 'withdrawn' || sheet.state === 'superseded' || sheet.state === 'changed')
    return (
      <Paper
        masthead={sheet.state === 'changed' ? 'THE DOCUMENT CHANGED' : sheet.state === 'withdrawn' ? 'WITHDRAWN' : 'REPLACED'}
        organisation={doc.organisation}
      >
        <Heading doc={doc} you={you} />
        {sheet.state === 'changed' ? (
          <>
            <p className="sg-stamp is-bad">
              <strong>This is not the file you were sent.</strong> It was replaced after your link
              was issued, so it cannot be signed here.
            </p>
            <p className="sg-note">
              Whoever asked for your approval has the same information. You will be sent a new link
              if it still needs you.
            </p>
            <p className="sg-print">
              asked against {inFours(sheet.fingerprint?.askedAgainst ?? '')}
              <br />
              file now {inFours(sheet.fingerprint?.now ?? '')}
            </p>
          </>
        ) : sheet.state === 'withdrawn' ? (
          <>
            <p className="sg-stamp is-waiting">
              <strong>This request was taken back.</strong> Nothing is being asked of you.
            </p>
            <p className="sg-note">It is being revised. If it comes back you will be sent a new link.</p>
          </>
        ) : (
          <>
            <p className="sg-stamp is-waiting">
              <strong>A newer version of this has replaced it.</strong>
            </p>
            <p className="sg-note">
              Nothing further is needed from you on this one. If the new version needs your approval
              you will be sent a link for it.
            </p>
          </>
        )}
      </Paper>
    );

  const answered = you.answer !== 'waiting' && !!you.at;
  /*
   * WHEN AN ANSWER STOPS BEING CHANGEABLE, which is Vinnie's call of 22 August
   * with the one boundary it needs to stay a signature.
   *
   * Signed off: sealed for everybody. A release cut on four approvals cannot
   * have one of them withdrawn afterwards.
   *
   * Rejected: sealed for everybody EXCEPT whoever rejected it. The document is
   * already going back to be revised, so nobody else's approval means anything
   * — but a rejection typed by mistake has to be undoable by the person who
   * typed it, or the only way back is to withdraw the request and ask four
   * people all over again.
   */
  const mineIsTheRejection = you.answer === 'rejected' && !!you.at;
  const sealed =
    sheet.state === 'signed' || (sheet.state === 'rejected' && !mineIsTheRejection);

  const open = async () => {
    setBusy(true);
    setProblem('');
    const got = await ask({ action: 'open' });
    setBusy(false);
    /*
     * A DOCUMENT returns a file address to SHOW HERE rather than open in a new
     * tab — window.open after an await is a popup the browser may block, and a
     * blocked popup reads as "the button does nothing" on the one press the whole
     * feature depends on. A SPRINT or a RELEASE has no file — its content came
     * down with the sheet — so opening it only records the review and unlocks the
     * answer. An error is still an error either way.
     */
    if (got.error || (subjectKind === 'document' && !got.url)) {
      setProblem(got.error ?? 'It could not be opened.');
      return;
    }
    if (got.url) {
      setFile(got.url);
      /* A SPREADSHEET cannot be drawn in place, so "open" means DOWNLOAD. The
         click that started this is the gesture the download rides on; a visible
         fallback link is rendered too, in case a browser still declines it. */
      if (signByUpload) {
        const a = document.createElement('a');
        a.href = got.url;
        a.download = doc.fileName || 'test-script.xlsx';
        a.rel = 'noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    }
    setSheet((s) => (s ? { ...s, you: { ...s.you!, opened: true } } : s));
  };

  /*
   * THE COMPLETED WORKBOOK, SENT BACK (spreadsheet test script, tester only).
   * The file becomes the document's signed version, so the /sign function
   * re-fingerprints the document to it and only then posts the downstream
   * approvers their links (the two-phase send). The page shows the completed
   * file and its execution summary from the reply; nothing is signed yet.
   */
  const upload = async (f: File) => {
    setUpErr('');
    if (!looksLikeWorkbook(f)) {
      setUpErr('That is not an Excel workbook. Upload the .xlsx you filled in.');
      return;
    }
    if (f.size > UPLOAD_CAP) {
      setUpErr('That workbook is over 25 MB. Save it smaller, or remove embedded images, and try again.');
      return;
    }
    setUploading(true);
    let data = '';
    try {
      data = await toBase64(f);
    } catch {
      setUploading(false);
      setUpErr('That file could not be read. Try choosing it again.');
      return;
    }
    /* Read the result in the browser, where the xlsx library already is; a failure
       here never blocks the upload (the record just carries no read-out). */
    const summary = await parseExecSummary(f);
    const got = await ask({
      action: 'upload',
      file: { name: f.name, type: f.type || XLSX_TYPES[0], size: f.size, data },
      ...(summary ? { summary } : {}),
    });
    setUploading(false);
    if (got.error) {
      setUpErr(got.error);
      return;
    }
    setSheet({ ...got, document: got.document ?? doc });
  };

  const record = async (answer: 'approved' | 'rejected') => {
    setBusy(true);
    setProblem('');
    const got = await ask({ action: 'answer', answer, why });
    setBusy(false);
    if (got.error) {
      setProblem(got.error);
      return;
    }
    setSaying(null);
    setWhy('');
    setSheet({ ...got, document: got.document ?? doc });
  };

  return (
    <Paper
      masthead={
        sheet.state === 'signed'
          ? 'SIGNED OFF'
          : sheet.state === 'rejected'
            ? 'SENT BACK'
            : signByUpload
              ? 'TEST SCRIPT SIGN-OFF'
              : 'APPROVAL REQUEST'
      }
      organisation={doc.organisation}
    >
      <Heading doc={doc} you={you} />

      {/*
        * THE SUBJECT, AND THE GATE ON IT.
        *
        * The two answers stay shut until this has been pressed. It costs one
        * press and buys the sentence an audit needs — this person reviewed this,
        * at this time, before they approved it. A DOCUMENT opens as a file; a
        * SPRINT or a RELEASE shows what is IN it, read-only, from the same press.
        * The gate is said ON the card rather than beside the buttons (Vinnie, 22
        * Aug), and it changes rather than vanishing, so the buttons never jump up
        * under a reaching hand.
        */}
      {subjectKind === 'document' && signByUpload ? (
        /*
         * A SPREADSHEET TEST SCRIPT — download, complete, upload, sign. An Excel
         * file has no in-browser view, so the PDF page's "Open the document" gate
         * becomes two plain steps for the tester, and one (download) for a later
         * approver who signs the completed workbook.
         */
        <>
          {mustUpload && uploaded ? (
            /* The completed workbook the tester sent back — it is now the record. */
            <div className="sg-done-file">
              <p className="sg-step-label">Your completed workbook</p>
              <div className="sg-file">
                <div className="sg-file-main">
                  <div className="sg-file-lead">
                    <span className="sg-done-tick">
                      <Icon name="check" size={17} />
                    </span>
                    <div className="sg-file-what">
                      <strong>{doc.fileName || 'Completed test script'}</strong>
                      <span>Excel · {size(doc.fileSize ?? 0)} · replaces the draft</span>
                    </div>
                  </div>
                  <button className="btn" onClick={open} disabled={busy}>
                    <Icon name="download" size={15} />
                    Download it
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* STEP 1 — download (the blank, for a tester; the completed workbook,
               for a later approver). A spreadsheet cannot be shown in place. */
            <div className="sg-file">
              <div className="sg-file-main">
                <div className="sg-file-what">
                  <strong>{doc.fileName || 'The test script'}</strong>
                  <span>Excel workbook · {size(doc.fileSize ?? 0)}</span>
                </div>
                <button className="btn" onClick={open} disabled={busy}>
                  <Icon name="download" size={15} />
                  {you.opened ? 'Download again' : 'Download the test script'}
                </button>
              </div>
              {!answered && !sealed && (
                <p className="sg-gate">
                  <Icon name="info" size={13} />
                  {mustUpload
                    ? 'A spreadsheet can’t be shown here — download it, run the tests and fill it in.'
                    : 'A spreadsheet can’t be shown here — download it to review before you sign off.'}
                </p>
              )}
            </div>
          )}

          {/* STEP 2 (tester only, before signing) — upload the completed workbook. */}
          {mustUpload && !uploaded && !answered && !sealed && (
            <div className="sg-upload">
              <p className="sg-step-label">
                Upload your completed workbook <span className="req">*</span>
              </p>
              <label className={`sg-drop${uploading ? ' is-busy' : ''}`}>
                <input
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    /* Let the same file be chosen again after an error. */
                    e.target.value = '';
                    if (f) void upload(f);
                  }}
                />
                <Icon name={uploading ? 'clock' : 'upload'} size={24} />
                <strong>{uploading ? 'Uploading…' : 'Choose your completed workbook'}</strong>
                <span>Excel, up to 25 MB. This becomes the signed record.</span>
              </label>
              {upErr && (
                <p className="sg-problem" role="alert">
                  {upErr}
                </p>
              )}
            </div>
          )}

          {/* YOU CAN LEAVE AND COME BACK — the tester is usually not a FlightDeck
              user and may close the window part-way. Nothing is lost: the same link
              re-opens this page, and the workbook is kept the instant it is uploaded.
              Shown to the tester until they have signed off. */}
          {mustUpload && !answered && !sealed && (
            <p className="sg-resume">
              <Icon name="info" size={13} />
              You can close this page and come back any time — just open the same link from your
              email. {uploaded
                ? 'Your completed workbook is saved; finish by signing off below.'
                : 'Nothing is lost, and your workbook is saved the moment you upload it.'}
            </p>
          )}

          {/* THE RESULT, READ FROM THE WORKBOOK — a picture, not a spreadsheet to
              open. Shown once there is a completed workbook: to the tester after
              they upload, and to every later approver (the file is already theirs). */}
          {summary && (uploaded || !mustUpload) && (
            <div className="sg-exec">
              <p className="sg-exec-head">
                Execution summary <span>· from the workbook</span>
              </p>
              <div className="sg-exec-stats">
                <span className="sg-exec-stat">
                  <b>{summary.cases}</b>
                  <span>{EXEC_LABELS.cases}</span>
                </span>
                <span className="sg-exec-stat is-pass">
                  <b>{summary.passed}</b>
                  <span>{EXEC_LABELS.passed}</span>
                </span>
                <span className="sg-exec-stat is-fail">
                  <b>{summary.failed}</b>
                  <span>{EXEC_LABELS.failed}</span>
                </span>
                <span className="sg-exec-stat is-defect">
                  <b>{summary.defects}</b>
                  <span>{EXEC_LABELS.defects}</span>
                </span>
                <span className="sg-exec-pct">
                  <b>{pctPassed(summary)}%</b>
                  <span>{EXEC_LABELS.passRate}</span>
                </span>
              </div>
              <div className="sg-exec-bar">
                <span className="is-pass" style={{ width: `${pctPassed(summary)}%` }} />
                <span className="is-fail" style={{ width: `${100 - pctPassed(summary)}%` }} />
              </div>
            </div>
          )}
        </>
      ) : subjectKind === 'document' ? (
        <div className="sg-file">
          <div className="sg-file-main">
            <div className="sg-file-what">
              <strong>{doc.fileName || 'The document'}</strong>
              <span>
                {(doc.fileType ?? '').includes('pdf') ? 'PDF' : doc.fileType || 'file'} ·{' '}
                {size(doc.fileSize ?? 0)}
              </span>
            </div>
            <button className="btn" onClick={open} disabled={busy}>
              {you.opened ? 'Show it again' : 'Open the document'}
            </button>
          </div>
          {!answered && !sealed && (
            <p className={`sg-gate${you.opened ? ' is-open' : ''}`}>
              <Icon name={you.opened ? 'check' : 'eye'} size={13} />
              {you.opened ? 'Opened — you can answer below' : 'Open the document to answer'}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="sg-file">
            <div className="sg-file-main">
              <div className="sg-file-what">
                <strong>{subjectKind === 'sprint' ? 'What this sprint delivered' : 'What’s shipping'}</strong>
                <span>
                  {doc.items?.length ?? 0} item{(doc.items?.length ?? 0) === 1 ? '' : 's'}
                  {subjectKind === 'sprint' && doc.startsOn && doc.endsOn
                    ? ` · ${onThe(doc.startsOn)} – ${onThe(doc.endsOn)}`
                    : ''}
                </span>
              </div>
              <button className="btn" onClick={open} disabled={busy || you.opened}>
                {you.opened
                  ? 'Shown below'
                  : subjectKind === 'sprint'
                    ? 'Show what’s included'
                    : 'Show what’s shipping'}
              </button>
            </div>
            {!answered && !sealed && (
              <p className={`sg-gate${you.opened ? ' is-open' : ''}`}>
                <Icon name={you.opened ? 'check' : 'eye'} size={13} />
                {you.opened ? 'Reviewed — you can answer below' : 'Review what’s included to answer'}
              </p>
            )}
          </div>
          {you.opened && (
            <ul className="sg-review">
              {(doc.items ?? []).map((it, i) => (
                <li key={`${it.ref}-${i}`} className="sg-del">
                  {it.ref && <span className="sg-del-ref">{it.ref}</span>}
                  <span className="sg-del-title">{it.title || 'Untitled'}</span>
                  {it.type && <span className="sg-del-type">{it.type}</span>}
                  {isDone(it.status) && (
                    <span className="sg-del-done">
                      <Icon name="check" size={12} /> Done
                    </span>
                  )}
                </li>
              ))}
              {(doc.items ?? []).length === 0 && (
                <li className="sg-del-empty">Nothing is listed on this yet.</li>
              )}
            </ul>
          )}
        </>
      )}
      {file && signByUpload ? (
        /* A spreadsheet is downloaded, not drawn — the fallback link if the
           browser declined the automatic download the button triggered. */
        <p className="sg-note">
          Your download should have started.{' '}
          <a href={file} rel="noreferrer" download={doc.fileName || 'test-script.xlsx'}>
            Download it again
          </a>{' '}
          if it did not.
        </p>
      ) : file ? (
        <>
          <object className="sg-viewer" data={file} type={doc.fileType || 'application/pdf'}>
            {/* A Word file, or a browser that will not draw a PDF in place.
                Never a dead end: the same address, as something to press. */}
            <p className="sg-note">
              This is not a document your browser can show here.{' '}
              <a href={file} target="_blank" rel="noreferrer">
                Open it in a new tab
              </a>
              .
            </p>
          </object>
          <p className="sg-note">
            <a href={file} target="_blank" rel="noreferrer">
              Open it in a new tab
            </a>{' '}
            to read, print or save it.
          </p>
        </>
      ) : null}

      <table className="sg-who">
        <thead>
          <tr>
            <th>Title</th>
            <th>Name</th>
            <th>Answer</th>
          </tr>
        </thead>
        <tbody>
          {panel.map((row, i) => (
            <tr key={`${row.name}-${i}`} className={row.isYou ? 'is-you' : undefined}>
              <td className="sg-role">{row.position}</td>
              <td className="sg-name">
                {row.name}
                {row.isYou && <span className="sg-tag">you</span>}
              </td>
              <td className="sg-answer">
                {row.answer === 'waiting' ? (
                  <span className="sg-pill is-waiting">Waiting</span>
                ) : row.answer === 'rejected' ? (
                  <>
                    <span className="sg-pill is-rejected">Rejected</span>{' '}
                    <span className="sg-date">{onThe(row.at)}</span>
                    {row.why && <em className="sg-why">{row.why}</em>}
                  </>
                ) : (
                  <>
                    <span className="sg-pill is-approved">Approved</span>{' '}
                    <span className="sg-date">{onThe(row.at)}</span>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sheet.state === 'rejected' && !answered && (
        <p className="sg-stamp is-bad">
          <strong>Somebody has sent this back.</strong> There is nothing to approve until it has
          been revised and sent again.
        </p>
      )}

      {answered ? (
        <>
          <p className={`sg-stamp ${you.answer === 'rejected' ? 'is-bad' : 'is-ok'}`}>
            <strong>
              You{' '}
              {you.answer === 'rejected'
                ? signByUpload
                  ? 'marked it as not passed'
                  : 'rejected this'
                : signByUpload
                  ? 'signed this off'
                  : 'approved this'}{' '}
              on {when(you.at)}.
            </strong>{' '}
            {you.answer === 'rejected'
              /* NOT "and whoever asked has been told" — nothing tells them yet.
                 That is the email half, and it waits on a mail key. */
              ? 'It goes back to be revised.'
              : /* A signed-off test script now moves to whoever is still to sign.
                   Naming them is the honest "what happens next" an approver asks. */
                nextToSign(panel) && sheet.state !== 'signed'
                ? `Nothing further is needed from you. It now goes to ${nextToSign(panel)}.`
                : 'Nothing further is needed from you.'}
          </p>
          {you.answer === 'rejected' && you.why && <p className="sg-note">You said: {you.why}</p>}
          {/* CHANGING YOUR MIND IS ALLOWED WHILE THE REQUEST IS OPEN — Vinnie,
              22 August — and stops the moment it has been relied on. Both
              answers stay in the trail either way; nothing is erased. */}
          {sealed ? (
            <p className="sg-note">
              {sheet.state === 'signed'
                ? 'Everybody has now answered and the document has been signed off, so answers can no longer be changed.'
                : 'This has been sent back, so answers can no longer be changed.'}
            </p>
          ) : saying ? (
            <Asking
              answer={saying}
              signByUpload={signByUpload}
              why={why}
              setWhy={setWhy}
              busy={busy}
              onCancel={() => setSaying(null)}
              onConfirm={() => record(saying)}
            />
          ) : (
            <div className="sg-act">
              <button className="link" onClick={() => setSaying(you.answer === 'approved' ? 'rejected' : 'approved')}>
                Change it to {you.answer === 'approved' ? noLabel.toLowerCase() : yesLabel.toLowerCase()}
              </button>
            </div>
          )}
        </>
      ) : sealed ? null : saying ? (
        <Asking
          answer={saying}
          signByUpload={signByUpload}
          why={why}
          setWhy={setWhy}
          busy={busy}
          onCancel={() => setSaying(null)}
          onConfirm={() => record(saying)}
        />
      ) : (
        <>
          <div className="sg-act">
            {/* NO DEFAULT IS CHOSEN. An "Approve" already selected is how a
                refusal gets recorded as agreement by somebody moving fast. */}
            <button className="sg-ans is-approved" disabled={!readyToAnswer || busy} onClick={() => setSaying('approved')}>
              {yesLabel}
            </button>
            <button className="sg-ans is-rejected" disabled={!readyToAnswer || busy} onClick={() => setSaying('rejected')}>
              {noLabel}
            </button>
          </div>
          {/* THE GATE, SAID ONCE MORE BY THE BUTTONS — a tester must upload first;
              everyone else must have opened the subject. Matches the subject-card
              gate in words, so the person is never told two different things. */}
          {!readyToAnswer && (
            <p className="sg-gate">
              <Icon name="lock" size={13} />
              {mustUpload
                ? 'Upload your completed workbook to sign off.'
                : signByUpload
                  ? 'Download the test script to sign off.'
                  : 'Open the document to answer.'}
            </p>
          )}
        </>
      )}

      {problem && (
        <p className="sg-problem" role="alert">
          {problem}
        </p>
      )}
    </Paper>
  );
}

/** The one confirmation, and the one question a rejection owes the sheet. */
function Asking({
  answer,
  signByUpload,
  why,
  setWhy,
  busy,
  onCancel,
  onConfirm,
}: {
  answer: 'approved' | 'rejected';
  signByUpload: boolean;
  why: string;
  setWhy: (s: string) => void;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const no = answer === 'rejected';
  return (
    <div className="sg-asking">
      <p className="sg-asking-head">
        {no ? (
          <>
            You are <strong>{signByUpload ? 'marking it as not passed' : 'rejecting this'}</strong>.
            Say why, so it can be put right.
          </>
        ) : (
          <>
            You are <strong>{signByUpload ? 'signing this off' : 'approving this'}</strong>. Your
            name and title go on the sheet against today&rsquo;s date.
          </>
        )}
      </p>
      {no && (
        <label className="sg-reason stack">
          {signByUpload ? 'Why didn’t it pass?' : 'Why are you rejecting it?'}{' '}
          <span className="req">*</span>
          <input
            className="input"
            value={why}
            autoFocus
            aria-required="true"
            placeholder={
              signByUpload
                ? 'e.g. three cases failed and the defects are not yet fixed'
                : 'e.g. the cancellation letter is not in scope'
            }
            onChange={(e) => setWhy(e.target.value)}
          />
        </label>
      )}
      <div className="sg-act">
        <button className="btn" disabled={busy || (no && why.trim() === '')} onClick={onConfirm}>
          {busy
            ? 'Recording…'
            : no
              ? signByUpload
                ? 'Record that it didn’t pass'
                : 'Record the rejection'
              : signByUpload
                ? 'Record the sign-off'
                : 'Record the approval'}
        </button>
        <button className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Who is being asked, and what for. */
function Heading({
  doc,
  you,
}: {
  doc: NonNullable<Sheet['document']>;
  you: NonNullable<Sheet['you']>;
}) {
  const KIND: Record<string, string> = {
    requirements: 'Requirements',
    change: 'Change request',
    test: 'Test script',
  };
  const subjectKind = doc.subjectKind ?? 'document';
  /* The line under the title names WHAT this is: a document by its kind, a sprint
     or a release by its own word. */
  const sub =
    subjectKind === 'sprint'
      ? 'Sprint sign-off'
      : subjectKind === 'release'
        ? 'Release sign-off'
        : KIND[doc.kind ?? ''] ?? doc.kind ?? 'Document';
  return (
    <>
      <p className="sg-title">{doc.title}</p>
      <p className="sg-sub">
        {sub}
        {doc.board ? ` · ${doc.board}` : ''}
        {subjectKind === 'document' && (doc.version ?? 1) > 1 ? ` · version ${doc.version}` : ''}
      </p>
      <div className="sg-to">
        <b>{you.name}</b>
        <span>
          {you.position}
          {doc.sentAt
            ? ` · sent for ${doc.kind === 'test' ? 'sign-off' : 'approval'} on ${onThe(doc.sentAt)}`
            : ''}
        </span>
      </div>
    </>
  );
}

/**
 * THE SHEET ITSELF, and the reason it does not look like the app.
 *
 * Somebody arriving here has never seen FlightDeck and is not being asked to
 * learn it. The chrome an approver needs is the chrome of a document: a
 * masthead saying whose it is, one column of paper, and nothing to navigate.
 */
function Paper({
  masthead,
  organisation,
  children,
}: {
  masthead: string;
  organisation?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sg">
      <div className="sg-paper">
        <div className="sg-top">
          <span>FLIGHTDECK · {masthead}</span>
          {organisation && <span>{organisation}</span>}
        </div>
        <div className="sg-body">{children}</div>
      </div>
      <p className="sg-foot">Recorded by FlightDeck. Your answer is kept with the record it was given against.</p>
    </div>
  );
}
